/**
 * AUTHOR: codewithshinde
 * Description:
 * references:
 * https://developer.mozilla.org/en-US/docs/Web/API/WebUSB_API
 * https://hackernoon.com/exploring-the-webusb-api-connecting-to-usb-devices-and-printing-with-tspltspl2
 * https://www.sabatino.dev/webusb-is-my-favorite-browser-api/
 */

class LightUSB {
  productId: number;
  vendorId: number;
  device: USBDevice | null = null; // Use USBDevice type for better type safety

  // New properties to store the successfully claimed interface and identified OUT endpoint
  private claimedPrintInterface: USBInterface | null = null;
  private outEndpoint: USBEndpoint | null = null;

  constructor(productId: number, vendorId: number) {
    this.productId = productId;
    this.vendorId = vendorId;
  }

  /**
   * Requests a USB device matching the specified productId and vendorId.
   * If a device is already paired, it will return that, otherwise, it will prompt the user.
   */
  getDevice = async (): Promise<USBDevice | null> => {
    try {
      // First, check if the device is already paired and set
      if (await this.isPaired()) {
        return this.device;
      }

      // If not paired or not set, request the device
      // @ts-ignore // This ignore is for `navigator.usb.requestDevice` if @types/w3c-web-usb isn't fully recognizing it in your setup
      const device = await navigator.usb.requestDevice({
        filters: [{ vendorId: this.vendorId, productId: this.productId }],
      });
      this.device = device;
      return device;
    } catch (e) {
      console.error("Failed to request device:", e);
      return null;
    }
  };

  /**
   * Checks if the USB device with the specified vendorId and productId is already paired/connected.
   * Updates `this.device` if found.
   */
  async isPaired(): Promise<boolean> {
    if (!this.isBrowserSupported()) {
      console.warn("WebUSB API is not supported.");
      return false;
    }
    // @ts-ignore
    const devices = await navigator.usb.getDevices();
    this.device =
      devices?.find(
        (device: USBDevice) =>
          device.vendorId === this.vendorId && device.productId === this.productId
      ) || null;

    return !!this.device;
  }

  /**
   * Retrieves all connected USB devices.
   */
  static getUsbDevices = async (): Promise<USBDevice[]> => {
    if (!("usb" in navigator)) {
      console.warn("WebUSB API is not supported in this browser.");
      return [];
    }
    // @ts-ignore
    return await navigator.usb.getDevices();
  };

  /**
   * Initiates the LightUSB instance by prompting the user to select a device.
   */
  static initiateBySelect = async (): Promise<LightUSB | null> => {
    try {
      if (!("usb" in navigator)) {
        throw new Error("WebUSB API is not supported!");
      }
      // @ts-ignore
      const device = await navigator?.usb.requestDevice({ filters: [] });
      if (!device) {
        console.error("No device selected");
        return null;
      }
      const lusb = new LightUSB(device?.productId, device?.vendorId);
      await lusb.applyDeviceSettings(device);
      return lusb;
    } catch (e) {
      console.error("Failed to initiate by select:", e);
      return null;
    }
  };

  /**
   * Applies device settings such as opening the device and claiming interfaces.
   */
  applyDeviceSettings = async (device: USBDevice | null = this.device): Promise<void> => {
    if (!device) {
      throw new Error("No device found to apply settings.");
    }
    this.device = device;
    if (!this.isDeviceOpen()) {
      try {
        await this.device.open();
        console.log("USB Device opened.");
      } catch (error) {
        console.error("Failed to open device:", error);
        throw new Error("Failed to open USB device. It might be in use or disconnected.");
      }
    }
    await this.claimInterfaces();
  };

  /**
   * Claims the necessary interfaces and identifies the OUT endpoint for printing.
   * This method tries to intelligently find the correct interface and endpoint.
   */
  private claimInterfaces = async (): Promise<void> => {
    if (!this.device) {
      throw new Error("No device to claim interfaces on.");
    }

    // Reset previously claimed interface and endpoint
    this.claimedPrintInterface = null;
    this.outEndpoint = null;

    // 1. Select Configuration
    // Most devices have a single configuration, usually value 1.
    // Check your device's USB descriptor (chrome://device-log) if 1 doesn't work.
    const desiredConfigurationValue = 1;

    if (!this.device.configuration || this.device.configuration.configurationValue !== desiredConfigurationValue) {
      try {
        console.log(`Selecting configuration ${desiredConfigurationValue}...`);
        await this.device.selectConfiguration(desiredConfigurationValue);
        console.log(`Configuration ${desiredConfigurationValue} selected.`);
      } catch (error) {
        console.error(`Failed to select configuration ${desiredConfigurationValue}:`, error);
        throw new Error(`Failed to select device configuration ${desiredConfigurationValue}. ` +
                        `Verify the printer's configuration value in chrome://device-log.`);
      }
    }

    // Ensure a configuration is now active
    const config = this.device.configuration;
    if (!config) {
      throw new Error("Device has no active configuration after selection attempt.");
    }

    let foundInterfaceWithEndpoint = false;

    // --- Strategy 1: Prioritize interface with Printer Class (0x07) ---
    const PRINTER_INTERFACE_CLASS = 7; // USB-IF defined class code for Printer

    for (const iface of config.interfaces) {
      for (const alt of iface.alternates) {
        // Check if this alternate setting is explicitly for a printer
        if (alt && alt.interfaceClass === PRINTER_INTERFACE_CLASS) {
          console.log(`Found printer-class interface: ${iface.interfaceNumber}. Checking endpoints...`);
          const outEndpoint = alt.endpoints.find(
            (ep) => ep.direction === "out" && ep.type === "bulk"
          );

          if (outEndpoint) {
            try {
              if (!iface.claimed) {
                await this.device.claimInterface(iface.interfaceNumber);
                console.log(`Printer-class Interface ${iface.interfaceNumber} claimed successfully.`);
              } else {
                console.log(`Printer-class Interface ${iface.interfaceNumber} already claimed.`);
              }
              this.claimedPrintInterface = iface;
              this.outEndpoint = outEndpoint;
              foundInterfaceWithEndpoint = true;
              console.log(`Found and prepared OUT endpoint ${this.outEndpoint.endpointNumber} on printer interface ${this.claimedPrintInterface.interfaceNumber}.`);
              return; // Success, exit
            } catch (error) {
              console.warn(`Failed to claim printer-class interface ${iface.interfaceNumber}:`, error);
              // Don't throw yet, try other interfaces if this one fails to claim
            }
          }
        }
      }
    }

    // --- Strategy 2: If no printer-class interface worked, search for any bulk OUT endpoint ---
    if (!foundInterfaceWithEndpoint) {
      console.log("No claimable printer-class interface with bulk OUT endpoint found. Searching all interfaces...");
      for (const iface of config.interfaces) {
        for (const alt of iface.alternates) {
          const outEndpoint = alt.endpoints.find(
            (ep) => ep.direction === "out" && ep.type === "bulk" // Printers usually use bulk endpoints
            // If your printer uses an Interrupt endpoint, you might change 'bulk' to 'interrupt' here.
          );
          if (outEndpoint) {
            try {
              if (!iface.claimed) {
                await this.device.claimInterface(iface.interfaceNumber);
                console.log(`Generic Interface ${iface.interfaceNumber} claimed.`);
              } else {
                console.log(`Generic Interface ${iface.interfaceNumber} already claimed.`);
              }
              this.claimedPrintInterface = iface;
              this.outEndpoint = outEndpoint;
              foundInterfaceWithEndpoint = true;
              console.log(`Found and prepared OUT endpoint ${this.outEndpoint.endpointNumber} on generic interface ${this.claimedPrintInterface.interfaceNumber}.`);
              return; // Success, exit
            } catch (error) {
              console.warn(`Failed to claim generic interface ${iface.interfaceNumber}:`, error);
              // Continue to try claiming other interfaces if this one fails
            }
          }
        }
      }
    }

    // If we reach here, no suitable interface and endpoint were found or claimed
    throw new Error("Could not find or claim a suitable interface with an OUT bulk endpoint for printing. " +
                    "Verify device configuration, interface class, endpoint type (e.g., bulk vs interrupt), and permissions using chrome://device-log.");
  };

  /**
   * Connects to the USB device.
   */
  connect = async (): Promise<{ status: boolean; error?: any; errorMessage?: string }> => {
    try {
      if (!this.isBrowserSupported()) {
        throw new Error("WebUSB API is not supported!");
      }
      // Check if device is already open and ready (including claimed interface/endpoint)
      if (this.isDeviceOpen() && this.claimedPrintInterface && this.outEndpoint) {
        console.log("Device already connected and ready.");
        return { status: true };
      }
      const device = await this.getDevice();
      if (!device) {
        throw new Error("No device found or selected for connection.");
      }
      await this.applyDeviceSettings(device);
      return { status: true };
    } catch (e: any) {
      console.error("Connection Error:", e);
      return {
        error: e,
        errorMessage: e.message || "Connection Error, Please contact Admin",
        status: false,
      };
    }
  };

  /**
   * Reconnects to the USB device. This is useful if the device becomes unresponsive.
   */
  reconnect = async (): Promise<{ status: boolean; error?: any; errorMessage?: string }> => {
    console.log("Attempting to reconnect...");
    try {
      // If already open and ready, no need to reconnect
      if (this.isDeviceOpen() && this.claimedPrintInterface && this.outEndpoint) {
        console.log("Device is already open and ready, no need to reconnect.");
        return { status: true };
      }
      // Try to get the device without prompting the user if it's already paired
      await this.isPaired(); // This will set this.device if it's found
      if (!this.device) {
        console.warn("No previously paired device found to reconnect to.");
        return { status: false, errorMessage: "No previously paired device found." };
      }
      await this.applyDeviceSettings(this.device); // This will re-open and re-claim interfaces
      console.log("Reconnection successful.");
      return { status: true };
    } catch (e: any) {
      console.error("Reconnection failed:", e);
      return {
        error: e,
        errorMessage: e.message || "Reconnection failed.",
        status: false,
      };
    }
  };

  /**
   * Performs a hard refresh: unpairs the device and then attempts to reconnect.
   * Useful for completely resetting the connection state.
   */
  hardRefresh = async (): Promise<{ status: boolean; error?: any; errorMessage?: string }> => {
    console.log("Performing hard refresh (unpair and repair)...");
    await this.unpair(); // Close and clear the device
    return await this.reconnect(); // Attempt to reconnect
  };

  /**
   * Checks the current connection status and attempts to reconnect if necessary.
   * This is a utility function to be called before operations like print.
   */
  private checkConnection = async (): Promise<boolean> => {
    // Check if device is open AND we have a claimed interface and out endpoint
    if (this.isDeviceOpen() && this.claimedPrintInterface && this.outEndpoint) {
      return true; // Device is fully connected and ready for transfer
    }

    console.warn("Device is not fully ready for printing. Attempting to reconnect/re-establish settings...");
    const reconnectResult = await this.reconnect();
    if (reconnectResult.status) {
      console.log("Device reconnected and settings (including interface/endpoint) applied.");
      // After reconnect, verify the endpoint is now available
      if (this.isDeviceOpen() && this.claimedPrintInterface && this.outEndpoint) {
        return true;
      } else {
        // This case should ideally not happen after a successful reconnect and applyDeviceSettings,
        // but included for robustness.
        throw new Error("Reconnection successful, but print endpoint still not found/claimed. Device configuration issue?");
      }
    } else {
      console.error("Failed to reconnect:", reconnectResult.errorMessage);
      throw new Error(
        reconnectResult.errorMessage || "Printer not responding. Please check connection."
      );
    }
  };

  /**
   * Prints the provided data to the connected USB device.
   */
  print = async (data: Uint8Array): Promise<void> => {
    try {
      await this.checkConnection(); // Ensure connection and endpoint are ready

      // Now we can safely use this.outEndpoint because checkConnection ensures it's set
      if (!this.device || !this.outEndpoint) {
        // This should theoretically not be hit if checkConnection passes, but good for type safety
        throw new Error("Printer not initialized or OUT endpoint not found.");
      }

      console.log(`Transferring data to endpoint ${this.outEndpoint.endpointNumber}...`);
      await this.device.transferOut(this.outEndpoint.endpointNumber, data);
      console.log("Print command sent successfully.");
    } catch (e) {
      console.error("Failed to print:", e);
      throw e; // Re-throw to allow calling code to handle the specific print error
    }
  };

  private isDeviceOpen = (): boolean => {
    return !!this.device && this.device.opened;
  };

  private isBrowserSupported = (): boolean => {
    return "usb" in navigator;
  };

  /**
   * Disconnects the USB device (closes the connection).
   */
  disconnect = async (): Promise<void> => {
    if (this.device && this.device.opened) {
      try {
        // Release all claimed interfaces before closing
        if (this.device.configuration) {
          for (const iface of this.device.configuration.interfaces) {
            if (iface.claimed) {
              await this.device.releaseInterface(iface.interfaceNumber);
              console.log(`Released interface ${iface.interfaceNumber}.`);
            }
          }
        }
        await this.device.close();
        console.log("Device disconnected.");
      } catch (error) {
        console.error("Error closing or releasing interface on device:", error);
      }
    }
    this.device = null; // Clear the device reference on disconnect
    this.claimedPrintInterface = null; // Also clear these on disconnect
    this.outEndpoint = null;
  };

  /**
   * Unpairs the device by closing the connection and clearing the device reference.
   * This allows the device to be re-selected or re-paired later.
   */
  unpair = async (): Promise<void> => {
    console.log("Unpairing device...");
    await this.disconnect(); // Use the existing disconnect logic
    console.log("Device unpaired.");
  };
}

export { LightUSB };